import React, { useState, useEffect } from 'react';
import { HelpingHand, Home, Send, AlertCircle, CheckCircle, MessageSquare, User, Mail, Phone, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AuthRequiredModal from '../components/AuthRequiredModal';

// Define the categories for prayer requests
const PRAYER_CATEGORIES = [
  { id: 'personal', label: 'Pessoal' },
  { id: 'family', label: 'Família' },
  { id: 'health', label: 'Saúde' },
  { id: 'financial', label: 'Financeiro' },
  { id: 'spiritual', label: 'Espiritual' },
  { id: 'professional', label: 'Profissional' },
  { id: 'other', label: 'Outro' }
];

interface GuidanceRequest {
  id: string;
  category: string;
  description: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  status: 'novo' | 'em_andamento' | 'respondido';
  created_at: string;
  response?: string;
  response_by?: string;
  response_at?: string;
}

export default function SpiritualGuidance() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [userRequests, setUserRequests] = useState<GuidanceRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<GuidanceRequest | null>(null);
  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    name: '',
    email: '',
    phone: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchUserRequests();
      loadUserProfile();
    }
  }, [isLoggedIn]);

  async function checkAuthStatus() {
    const { data: { session } } = await supabase.auth.getSession();
    setIsLoggedIn(!!session);
  }

  async function loadUserProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setFormData(prev => ({
          ...prev,
          name: user.user_metadata?.name || '',
          email: user.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  async function fetchUserRequests() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('spiritual_guidance_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isLoggedIn) {
      setIsAuthModalOpen(true);
      return;
    }

    // Validate form
    if (!formData.category) {
      setError('Por favor, selecione uma categoria');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Por favor, descreva seu pedido ou pergunta');
      return;
    }
    
    if (!formData.name.trim()) {
      setError('Por favor, informe seu nome');
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Por favor, informe seu email');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('Por favor, informe seu telefone');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('spiritual_guidance_requests')
        .insert([
          {
            category: formData.category,
            description: formData.description,
            user_id: user.id,
            user_name: formData.name,
            user_email: formData.email,
            user_phone: formData.phone,
            status: 'novo'
          }
        ])
        .select();

      if (error) throw error;

      setSuccess('Seu pedido foi enviado com sucesso! Um líder da igreja entrará em contato em breve.');
      setFormData({
        category: '',
        description: '',
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      });
      
      fetchUserRequests();
      setShowForm(false);
    } catch (error: any) {
      setError(error.message || 'Ocorreu um erro ao enviar seu pedido. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const viewRequest = (request: GuidanceRequest) => {
    setSelectedRequest(request);
    setShowForm(false);
  };

  const formatPhone = (value: string) => {
    // Remove tudo que não for número
    const numbers = value.replace(/\D/g, '');
    
    // Formata o número
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formattedPhone }));
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'novo': return 'Novo';
      case 'em_andamento': return 'Em análise';
      case 'respondido': return 'Respondido';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'novo': return 'bg-blue-100 text-blue-800';
      case 'em_andamento': return 'bg-yellow-100 text-yellow-800';
      case 'respondido': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (categoryId: string) => {
    const category = PRAYER_CATEGORIES.find(cat => cat.id === categoryId);
    return category ? category.label : categoryId;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <HelpingHand className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Orientação Espiritual</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Coluna da esquerda - Navegação e histórico */}
        <div className="md:col-span-1">
          <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
            <h2 className="text-xl font-semibold text-brand-teal mb-4">Orientação Espiritual</h2>
            <p className="text-gray-600 mb-6">
              Compartilhe suas dúvidas, pedidos de oração ou questões espirituais. Nossa equipe de líderes está pronta para orar por você e oferecer orientação baseada na Palavra de Deus.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowForm(true);
                  setSelectedRequest(null);
                }}
                className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                  showForm
                    ? 'bg-brand-light text-brand-teal'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <MessageSquare className="h-5 w-5" />
                <span className="font-medium">Novo Pedido</span>
              </button>
              
              <button
                onClick={() => {
                  setShowForm(false);
                  setSelectedRequest(null);
                }}
                className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                  !showForm && !selectedRequest
                    ? 'bg-brand-light text-brand-teal'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Clock className="h-5 w-5" />
                <span className="font-medium">Histórico</span>
              </button>
            </div>
          </div>
          
          {/* Informações de contato */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-lg font-semibold text-brand-teal mb-4">Contato Direto</h2>
            <p className="text-gray-600 mb-4">
              Para assuntos urgentes ou que exigem atenção imediata, entre em contato diretamente com nossa equipe pastoral:
            </p>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-brand-teal" />
                <span>(61) 98765-4321</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand-teal" />
                <span>pastoral@sdtbsb.com.br</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Coluna da direita - Formulário ou detalhes */}
        <div className="md:col-span-2">
          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
              <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {showForm ? (
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-semibold text-brand-teal mb-6">Enviar Pedido</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Categoria */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {PRAYER_CATEGORIES.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Descrição */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    placeholder="Descreva seu pedido de oração ou dúvida espiritual em detalhes..."
                    required
                  />
                </div>
                
                {/* Informações de contato */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Nome *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                        placeholder="Seu nome completo"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                        placeholder="seu@email.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                        placeholder="(00) 00000-0000"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Política de privacidade */}
                <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
                  <p>
                    Ao enviar este formulário, você concorda com nossa política de privacidade. 
                    Seus dados serão usados apenas para responder à sua solicitação e não serão 
                    compartilhados com terceiros.
                  </p>
                </div>
                
                {/* Botão de envio */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-teal text-white py-3 px-4 rounded-xl hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      <span>Enviar Pedido</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          ) : selectedRequest ? (
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-brand-teal">{getCategoryLabel(selectedRequest.category)}</h2>
                  <p className="text-sm text-gray-500">
                    Enviado em {new Date(selectedRequest.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(selectedRequest.status)}`}>
                  {getStatusLabel(selectedRequest.status)}
                </span>
              </div>
              
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Descrição</h3>
                <p className="text-gray-700 whitespace-pre-line">{selectedRequest.description}</p>
              </div>
              
              {selectedRequest.response && (
                <div className="mb-6 p-4 bg-brand-light rounded-lg">
                  <h3 className="font-medium text-brand-teal mb-2">Resposta</h3>
                  <p className="text-gray-700 whitespace-pre-line">{selectedRequest.response}</p>
                  {selectedRequest.response_at && (
                    <p className="text-sm text-gray-500 mt-2">
                      Respondido em {new Date(selectedRequest.response_at).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex justify-between">
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Voltar
                </button>
                
                {selectedRequest.status !== 'respondido' && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors"
                  >
                    Novo Pedido
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h2 className="text-xl font-semibold text-brand-teal mb-6">Histórico de Pedidos</h2>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
                </div>
              ) : userRequests.length > 0 ? (
                <div className="space-y-4">
                  {userRequests.map(request => (
                    <div 
                      key={request.id} 
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => viewRequest(request)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-medium text-brand-teal">{getCategoryLabel(request.category)}</h3>
                          <p className="text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(request.status)}`}>
                          {getStatusLabel(request.status)}
                        </span>
                      </div>
                      <p className="text-gray-700 line-clamp-2">{request.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">
                    Você ainda não enviou nenhum pedido de orientação espiritual.
                  </p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors"
                  >
                    Enviar Primeiro Pedido
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de autenticação necessária */}
      <AuthRequiredModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        message="Para enviar um pedido de orientação espiritual, você precisa estar logado."
        returnTo="/spiritual-guidance"
      />
    </div>
  );
}